import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { REGISTER } from '../constants/api';
import { CSS_CLASSES, FILE_PATHS } from '../constants/app';
import { Spinner } from './loading/LoadingIndicators';

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    hospitalName: '',
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({
    hospitalName: '',
    email: '',
    password: '',
    general: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  // Email validation regex
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  // Validation functions
  const validateForm = () => {
    const newErrors = {
      hospitalName: '',
      email: '',
      password: '',
      general: ''
    };

    // Hospital name validation
    if (!formData.hospitalName.trim()) {
      newErrors.hospitalName = 'Hospital name is required';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else if (formData.email.toLowerCase() === 'sgupta@gmail.com') {
      newErrors.email = 'This email is already registered, please use another email';
    }

    // Password validation
    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    setErrors(newErrors);
    return !newErrors.hospitalName && !newErrors.email && !newErrors.password;
  };

  // Handle input changes
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear field-specific error when user starts typing
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Handle form submission
  const handleRegister = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors(prev => ({ ...prev, general: '' }));

    try {
       const response = await fetch(REGISTER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies for authentication
         body: JSON.stringify({
           full_name: formData.hospitalName,
           email: formData.email,
           password: formData.password
         })
      });

       if (!response.ok) {
         const errorData = await response.json();
         
         // Check if email already exists error
         if (response.status === 400) {
           if (errorData.error && errorData.error === 'Email already exists') {
             setErrors(prev => ({
               ...prev,
               email: 'This email is already registered, please use another email'
             }));
           } else {
             setErrors(prev => ({
               ...prev,
               general: errorData.error || errorData.message || 'Registration failed. Please try again.'
             }));
           }
         } else {
           throw new Error(`Registration failed with status: ${response.status}`);
         }
         return;
       }

      await response.json();
      
      // Navigate to login page on success
      navigate('/');
      
    } catch (error) {
      console.error('Registration error:', error);
      setErrors(prev => ({
        ...prev,
        general: 'Registration failed. Please check your connection and try again.'
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleRegister();
    }
  };

  const inputClassName = (fieldError: string) =>
    `${CSS_CLASSES.INPUTS.PATIENT} ${fieldError ? CSS_CLASSES.INPUTS.ERROR : ''}`;

  return (
    <div className={CSS_CLASSES.AUTH.PAGE}>
      <div className={CSS_CLASSES.AUTH.CARD}>
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mb-4">
            <img 
              src={FILE_PATHS.IMAGES.HELIUS_LOGO}
              alt="Helius" 
              className="w-24 h-24 sm:w-30 sm:h-30 mx-auto"
            />
          </div>
          <p className={CSS_CLASSES.AUTH.TITLE}>Join Helius Clinician Platform</p>
        </div>

        {/* Registration Form */}
        <div className="space-y-6" onKeyDown={handleKeyPress}>
          {/* General Error Message */}
          {errors.general && (
            <div className={`${CSS_CLASSES.TEXT.ERROR} text-center`}>
              {errors.general}
            </div>
          )}

           {/* Hospital Name Field */}
           <div>
             <label className={CSS_CLASSES.AUTH.LABEL}>
               Hospital Name
             </label>
             <input
               type="text"
               value={formData.hospitalName}
               onChange={(e) => handleInputChange('hospitalName', e.target.value)}
               placeholder="Enter your hospital name"
               className={inputClassName(errors.hospitalName)}
               disabled={isLoading}
             />
             {errors.hospitalName && (
               <p className={`mt-1 ${CSS_CLASSES.TEXT.ERROR}`}>{errors.hospitalName}</p>
             )}
           </div>

          {/* Email Field */}
          <div>
            <label className={CSS_CLASSES.AUTH.LABEL}>
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="Enter your email address"
              className={inputClassName(errors.email)}
              disabled={isLoading}
            />
            {errors.email && (
              <p className={`mt-1 ${CSS_CLASSES.TEXT.ERROR}`}>{errors.email}</p>
            )}
          </div>

          {/* Password Field */}
          <div>
            <label className={CSS_CLASSES.AUTH.LABEL}>
              Password
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              placeholder="Enter your password (min 8 characters)"
              className={inputClassName(errors.password)}
              disabled={isLoading}
            />
            {errors.password && (
              <p className={`mt-1 ${CSS_CLASSES.TEXT.ERROR}`}>{errors.password}</p>
            )}
          </div>

          {/* Register Button */}
          <button
            type="button"
            onClick={handleRegister}
            disabled={isLoading}
            className={`${CSS_CLASSES.AUTH.SUBMIT} ${
              isLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isLoading ? (
              <span className="inline-flex items-center justify-center gap-2">
                <Spinner size="md" variant="onPrimary" />
                Creating Account...
              </span>
            ) : (
              'Register'
            )}
          </button>

          {/* Login Link */}
          <div className="text-center">
            <p className={CSS_CLASSES.AUTH.TITLE}>
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => navigate('/')}
                className={CSS_CLASSES.AUTH.LINK}
              >
                Sign in here
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
